import moment from 'moment';
import type { Moment } from 'moment';

// Configure moment globally
moment.locale('en');

/**
 * Time conversion and formatting utilities using moment.js
 */
export class TimeUtils {
  /**
   * Get current timestamp
   */
  static now(): Moment {
    return moment();
  }

  /**
   * Parse date from various formats
   */
  static parse(date: string | Date | number): Moment {
    return moment(date);
  }

  /**
   * Format date to common formats
   */
  static format(
    date: string | Date | number | Moment,
    format: string = 'YYYY-MM-DD HH:mm:ss'
  ): string {
    return moment(date).format(format);
  }

  /**
   * Common date formats
   */
  static formatters = {
    date: (date: string | Date | number | Moment) => moment(date).format('YYYY-MM-DD'),
    time: (date: string | Date | number | Moment) => moment(date).format('HH:mm:ss'),
    dateTime: (date: string | Date | number | Moment) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
    readable: (date: string | Date | number | Moment) => moment(date).format('MMM DD, YYYY'),
    full: (date: string | Date | number | Moment) =>
      moment(date).format('dddd, MMMM DD, YYYY HH:mm:ss'),
    iso: (date: string | Date | number | Moment) => moment(date).toISOString(),
    unix: (date: string | Date | number | Moment) => moment(date).unix(),
  };

  /**
   * Time zone conversions
   */
  static timezone = {
    toUTC: (date: string | Date | number | Moment) => moment(date).utc(),
    toLocal: (date: string | Date | number | Moment) => moment(date).local(),
    toTimezone: (date: string | Date | number | Moment, timezone: string) =>
      moment(date).utc().utcOffset(timezone),
  };

  /**
   * Date arithmetic
   */
  static add(
    date: string | Date | number | Moment,
    amount: number,
    unit: moment.unitOfTime.DurationConstructor
  ): Moment {
    return moment(date).add(amount, unit);
  }

  static subtract(
    date: string | Date | number | Moment,
    amount: number,
    unit: moment.unitOfTime.DurationConstructor
  ): Moment {
    return moment(date).subtract(amount, unit);
  }

  /**
   * Date comparisons
   */
  static isAfter(
    date1: string | Date | number | Moment,
    date2: string | Date | number | Moment
  ): boolean {
    return moment(date1).isAfter(moment(date2));
  }

  static isBefore(
    date1: string | Date | number | Moment,
    date2: string | Date | number | Moment
  ): boolean {
    return moment(date1).isBefore(moment(date2));
  }

  static isSame(
    date1: string | Date | number | Moment,
    date2: string | Date | number | Moment,
    unit?: moment.unitOfTime.StartOf
  ): boolean {
    return moment(date1).isSame(moment(date2), unit);
  }

  static isBetween(
    date: string | Date | number | Moment,
    start: string | Date | number | Moment,
    end: string | Date | number | Moment
  ): boolean {
    return moment(date).isBetween(moment(start), moment(end));
  }

  /**
   * Relative time
   */
  static fromNow(date: string | Date | number | Moment): string {
    return moment(date).fromNow();
  }

  static toNow(date: string | Date | number | Moment): string {
    return moment(date).toNow();
  }

  static duration(
    start: string | Date | number | Moment,
    end: string | Date | number | Moment
  ): moment.Duration {
    return moment.duration(moment(end).diff(moment(start)));
  }

  /**
   * Validation
   */
  static isValid(date: string | Date | number | Moment): boolean {
    return moment(date).isValid();
  }

  /**
   * Calendar operations
   */
  static startOf(date: string | Date | number | Moment, unit: moment.unitOfTime.StartOf): Moment {
    return moment(date).startOf(unit);
  }

  static endOf(date: string | Date | number | Moment, unit: moment.unitOfTime.StartOf): Moment {
    return moment(date).endOf(unit);
  }

  /**
   * Common business date operations
   */
  static business = {
    isWeekend: (date: string | Date | number | Moment) => {
      const day = moment(date).day();
      return day === 0 || day === 6; // Sunday or Saturday
    },
    isWeekday: (date: string | Date | number | Moment) => {
      const day = moment(date).day();
      return day >= 1 && day <= 5; // Monday to Friday
    },
    nextBusinessDay: (date: string | Date | number | Moment) => {
      let next = moment(date).add(1, 'day');
      while (TimeUtils.business.isWeekend(next)) {
        next = next.add(1, 'day');
      }
      return next;
    },
    previousBusinessDay: (date: string | Date | number | Moment) => {
      let prev = moment(date).subtract(1, 'day');
      while (TimeUtils.business.isWeekend(prev)) {
        prev = prev.subtract(1, 'day');
      }
      return prev;
    },
  };

  /**
   * Age calculation
   */
  static getAge(birthDate: string | Date | number | Moment): number {
    return moment().diff(moment(birthDate), 'years');
  }

  /**
   * Quarter operations
   */
  static quarter = {
    current: () => moment().quarter(),
    of: (date: string | Date | number | Moment) => moment(date).quarter(),
    start: (date: string | Date | number | Moment) => moment(date).startOf('quarter'),
    end: (date: string | Date | number | Moment) => moment(date).endOf('quarter'),
  };
}

// Export moment instance for direct usage
export { moment };

// Export commonly used functions
export const {
  now,
  parse,
  format,
  formatters,
  timezone,
  add,
  subtract,
  isAfter,
  isBefore,
  isSame,
  isBetween,
  fromNow,
  toNow,
  duration,
  isValid,
  startOf,
  endOf,
  business,
  getAge,
  quarter,
} = TimeUtils;
